/**
 * src/diagnostics/diagnosticsClient.js
 *
 * Tenant-scoped opt-in diagnostics client. See issue #732.
 *
 * Usage:
 *   configureDiagnostics({ tenantId, userId, config })   // wire from tenant/session
 *   logDiag('persist.start', { source: 'timer' })        // no-op unless enabled
 *   flushDiag()                                          // fire-and-forget; safe anytime
 *
 * Contract:
 *   - Zero network calls when the tenant's diagnostics flag is off or expired.
 *   - In-memory queue, flushed every 3s, on size >= 50, or on beforeunload.
 *   - Silent failure — never throws into callers.
 *   - Payloads are sanitized of obvious PII at log time (see scrubPayload).
 */

import { azurePost } from '../api/azureClient.js';

const FLUSH_INTERVAL_MS = 3000;
const MAX_QUEUE_SIZE = 50;
const MAX_PAYLOAD_BYTES = 4096;

/**
 * How many recent events the always-on trail keeps. Enough to cover a whole
 * save cycle and the conflict resolution after it; small enough that an
 * attached report stays readable in a GitHub issue.
 */
const MAX_RECENT = 40;

/** Always-on, in-memory only, never flushed to the diagnostics endpoint. */
const _recent = [];

// Keys stripped from payloads before they are queued. This is a defence-in-depth
// check — callers must not pass PII in the first place, but we also strip it.
const BLOCKED_KEYS = new Set([
  'password',
  'pwd',
  'secret',
  'token',
  'jwt',
  'authorization',
  'email',
  'emailAddress',
  'phone',
  'ssn',
  'dob',
  'content',
  'body',
  'plainText',
  'text',
  'html',
  'document',
]);

let _tenantId = null;
let _userId = null;
let _enabled = false;
let _expiresAt = 0;

const _queue = [];
let _flushTimer = null;
let _unloadBound = false;

function diagnosticsEnabled() {
  if (!_enabled) return false;
  if (!_expiresAt) return false;
  return _expiresAt > Date.now();
}

/**
 * Whether the current tenant has diagnostics switched on right now. Exported
 * for call sites that gate a `console.log` (visible in-browser only, no
 * network egress) behind the same tenant opt-in `logDiag` already uses —
 * never a reason to bypass `logDiag`'s own scrubbing/queueing for anything
 * that should actually leave the browser.
 */
export function isDiagEnabled() {
  return diagnosticsEnabled();
}

function scrubPayload(value, depth = 0) {
  if (value === null || value === undefined) return value;
  if (depth > 4) return '[depth]';
  const t = typeof value;
  if (t === 'string') {
    return value.length > 256 ? `${value.slice(0, 256)}…[${value.length}]` : value;
  }
  if (t === 'number' || t === 'boolean') return value;
  if (Array.isArray(value)) return value.slice(0, 20).map((v) => scrubPayload(v, depth + 1));
  if (t === 'object') {
    const out = {};
    for (const [key, val] of Object.entries(value)) {
      if (BLOCKED_KEYS.has(key)) {
        out[key] = '[scrubbed]';
        continue;
      }
      out[key] = scrubPayload(val, depth + 1);
    }
    return out;
  }
  return String(value);
}

function scheduleFlush() {
  if (_flushTimer) return;
  _flushTimer = setTimeout(() => {
    _flushTimer = null;
    flushDiag();
  }, FLUSH_INTERVAL_MS);
}

function bindUnloadOnce() {
  if (_unloadBound) return;
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeunload', () => {
    flushDiag();
  });
  window.addEventListener('seasuite:session-expired', () => {
    // Record the event before the session is gone; flushDiag will still try
    // to POST with the stale token — if the server rejects we lose this event,
    // which is acceptable (fire-and-forget).
    logDiag('session.expired', {});
    flushDiag();
  });
  _unloadBound = true;
}

/**
 * Configure (or reconfigure) the diagnostics client. Safe to call repeatedly.
 *
 * @param {object} args
 * @param {string|null} args.tenantId
 * @param {string|null} args.userId
 * @param {object|null} args.config — { enabled, expiresAt, enabledBy } from the registry
 */
export function configureDiagnostics({ tenantId = null, userId = null, config = null } = {}) {
  bindUnloadOnce();
  _tenantId = tenantId || null;
  _userId = userId || null;

  const enabled = !!(config && config.enabled);
  const expiresAt = config && config.expiresAt ? Date.parse(config.expiresAt) : 0;

  const wasOn = diagnosticsEnabled();
  _enabled = enabled;
  _expiresAt = Number.isFinite(expiresAt) ? expiresAt : 0;
  const isOn = diagnosticsEnabled();

  if (!isOn) {
    // Flipping off — drop queued events so we don't retain anything unexpected.
    _queue.length = 0;
    if (_flushTimer) {
      clearTimeout(_flushTimer);
      _flushTimer = null;
    }
  } else if (!wasOn) {
    // Flipping on — emit a session-start marker to anchor the trace.
    logDiag('diag.session.start', {
      ua: typeof navigator !== 'undefined' ? String(navigator.userAgent || '').slice(0, 200) : '',
    });
  }
}

/**
 * Record a diagnostic event. No-op unless diagnostics is enabled for the tenant.
 */
export function logDiag(eventType, payload = {}) {
  if (typeof eventType !== 'string' || !eventType) return;

  let scrubbed;
  try {
    scrubbed = scrubPayload(payload);
  } catch {
    scrubbed = { _scrubError: true };
  }

  // The trail is kept even when the tenant's diagnostics flag is off, because
  // that flag governs a NETWORK EGRESS and this buffer does not leave the
  // browser on its own. Without it, the single most informative thing about a
  // defect — what the save cycle was doing in the seconds before it — exists
  // only for a tenant that had opted in BEFOREHAND, which no tenant hitting a
  // defect for the first time ever has. recentDiag() hands the trail to the
  // conflict reporter, which is the sanctioned egress (errorReporter ->
  // error-log-ingest) the product already uses for background capture.
  //
  // Scrubbed BEFORE it is buffered, never on the way out, so nothing
  // unscrubbed is ever resident.
  _recent.push({
    eventType: eventType.slice(0, 64),
    payload: scrubbed,
    ts: new Date().toISOString(),
  });
  if (_recent.length > MAX_RECENT) _recent.shift();

  if (!diagnosticsEnabled()) return;

  // Guard against oversize payloads before they enter the queue.
  let serialized = '';
  try {
    serialized = JSON.stringify(scrubbed);
  } catch {
    serialized = '{}';
  }
  if (serialized.length > MAX_PAYLOAD_BYTES) {
    scrubbed = { _truncated: true, bytes: serialized.length };
  }

  _queue.push({
    eventType: eventType.slice(0, 64),
    payload: scrubbed,
    ts: new Date().toISOString(),
  });

  if (_queue.length >= MAX_QUEUE_SIZE) {
    flushDiag();
  } else {
    scheduleFlush();
  }
}

/**
 * Drain the in-memory queue to the server. Fire-and-forget; swallows errors.
 */
export function flushDiag() {
  if (_flushTimer) {
    clearTimeout(_flushTimer);
    _flushTimer = null;
  }
  if (!_queue.length) return;
  if (!diagnosticsEnabled()) {
    _queue.length = 0;
    return;
  }

  const events = _queue.splice(0, _queue.length);
  const body = _tenantId ? { tenantId: _tenantId, events } : { events };

  // Fire-and-forget — swallow errors to avoid impacting UX.
  azurePost('/api/diagnostics', body).catch(() => {
    /* swallow */
  });
}

/**
 * The last few diagnostic events, oldest first, whether or not the tenant has
 * diagnostics enabled. Returns copies — callers must not mutate the trail.
 *
 * Only the conflict reporter uses this. It is deliberately NOT flushed to the
 * diagnostics endpoint: that egress is the tenant's opt-in and this is not
 * covered by it.
 */
export function recentDiag(limit = MAX_RECENT) {
  const n = Math.max(0, Math.min(limit, _recent.length));
  return _recent.slice(_recent.length - n).map((e) => ({ ...e }));
}

/** Test seam — drop the always-on trail. */
export function _resetRecentDiagForTests() {
  _recent.length = 0;
}

/**
 * Test helper — exposes the current enabled state without leaking internals.
 * Not used in production code paths.
 */
export function _diagEnabledForTests() {
  return diagnosticsEnabled();
}
